/**
 * How strong a signal a velocity is sounded at.
 *
 * A velocity is how hard a note is played, not how strong its signal is, and the two are not
 * in proportion: heard loudness grows roughly as the signal's amplitude to the power 0.6, so a
 * velocity sounded as that fraction of full scale crowds the soft marks together and leaves
 * *pp* to *ff* sounding like two steps rather than six. Roger Dannenberg measured what
 * synthesizers do instead and proposed the mapping used here, a square law over a fixed range
 * of decibels, under which equal steps of velocity are heard as roughly equal steps of loudness
 * (R. Dannenberg, "The Interpretation of MIDI Velocity", ICMC 2006,
 * https://www.cs.cmu.edu/~rbd/papers/velocity-icmc2006.pdf):
 *
 *   amplitude = (m * v + b)^2,  b = 127 / (126 * sqrt(r)) - 1 / 126,  m = (1 - b) / 127
 *
 * with `r` the ratio of the loudest amplitude to the softest, over velocities 1 to 127. The
 * range is his recommendation of 60 dB. Written for velocities from 0 to 1, `m * v` is
 * `(1 - b) * v`.
 *
 * This is applied where a note is sounded and nowhere earlier, because a velocity is also what
 * a MIDI file stores, and a synthesizer reading the file applies its own curve to it.
 */

/** The ratio of the loudest amplitude to the softest, as its square root: 60 dB. */
const ROOT = 10 ** (60 / 40);

const B = 127 / (126 * ROOT) - 1 / 126;

/** The amplitude, 0 to 1, a velocity from 0 to 1 is sounded at. */
export function gainOf(velocity: number): number {
  if (velocity <= 0) return 0;
  return ((1 - B) * Math.min(1, velocity) + B) ** 2;
}
